import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { useUserData } from '../../hooks/useUserData';
import { useWeb3 } from '../../context/Web3Context';
import { useNotify } from '../../context/NotificationContext';
import { formatUsdc, formatZenova, formatBps, bnToNumber, formatTimestamp } from '../../utils/format';
import { PACKAGE_NAMES, INV_TYPES, NUM_PACKAGES } from '../../utils/constants';
import { NetworkGuard } from '../wallet/NetworkGuard';
import { InlineLoader, ButtonSpinner } from '../common/Loader';
import { EmptyState, Alert } from '../common/StatBox';
import { parseError } from '../../utils/errors';
import { ADDR } from '../../config/contracts';
import { readStoredRef, clearStoredRef } from '../../utils/referral';
import { sendTx } from '../../utils/txHelpers';

export const Invest = () => (
  <NetworkGuard>
    <InvestInner />
  </NetworkGuard>
);

const InvestInner = () => {
  const [tab, setTab] = useState('packages');

  return (
    <div>
      <div className="mtabs">
        <button className={`mtab ${tab === 'packages' ? 'active' : ''}`} onClick={() => setTab('packages')}>📦 Packages</button>
        <button className={`mtab ${tab === 'mine' ? 'active' : ''}`} onClick={() => setTab('mine')}>💎 My Investments</button>
      </div>

      {tab === 'packages' && <PackagesTab />}
      {tab === 'mine' && <MyInvestmentsTab />}
    </div>
  );
};

const PackagesTab = () => {
  const { account } = useWeb3();
  const { viewsR, logicW, usdcR, usdcW, zenovaR, zenovaW } = useContracts();
  const { isRegistered, refresh } = useUserData();
  const notify = useNotify();

  const [packages, setPackages] = useState(null);
  const [zenovaQuotes, setZenovaQuotes] = useState(null); // ZENOVA required per package
  const [usdcBalance, setUsdcBalance] = useState(null);
  const [zenovaBalance, setZenovaBalance] = useState(null);
  const [usdcAllowance, setUsdcAllowance] = useState(null);
  const [zenovaAllowance, setZenovaAllowance] = useState(null);
  const [referrerId, setReferrerId] = useState('');
  const [busyKey, setBusyKey] = useState(null);   // e.g. "3-usdc" or "3-zenova"
  const [busyLabel, setBusyLabel] = useState('');
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (isRegistered) return;
    const stored = readStoredRef();
    const urlRef = new URLSearchParams(window.location.search).get('ref');
    const r = stored || urlRef || '';
    if (r) setReferrerId(r);
  }, [isRegistered]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!viewsR) return;
      try {
        const pkgs = await viewsR.getAllPackages();
        if (cancelled) return;
        setPackages(pkgs);

        // Fetch ZENOVA quotes for each package
        const quotePromises = [];
        for (let i = 0; i < NUM_PACKAGES; i++) {
          quotePromises.push(viewsR.getPackageZenovaQuote(i).catch(() => null));
        }
        const quotes = await Promise.all(quotePromises);
        if (cancelled) return;
        setZenovaQuotes(quotes);

        if (account && usdcR && zenovaR) {
          const [ub, zb, ua, za] = await Promise.all([
            usdcR.balanceOf(account),
            zenovaR.balanceOf(account),
            usdcR.allowance(account, ADDR.storage),
            zenovaR.allowance(account, ADDR.storage)
          ]);
          if (cancelled) return;
          setUsdcBalance(ub);
          setZenovaBalance(zb);
          setUsdcAllowance(ua);
          setZenovaAllowance(za);
        }
      } catch (e) {}
    };
    load();
    return () => { cancelled = true; };
  }, [viewsR, usdcR, zenovaR, account, reload]);

  const payAndInvest = async (pkgIdx, useZenova) => {
    const price = packages.prices[pkgIdx];
    const zAmount = zenovaQuotes && zenovaQuotes[pkgIdx] ? zenovaQuotes[pkgIdx].zenovaRequired : null;

    // Balance check
    if (useZenova) {
      if (!zenovaBalance || !zAmount || zenovaBalance.lt(zAmount)) {
        notify.error(`Insufficient ZENOVA (need ${formatZenova(zAmount)})`);
        return;
      }
    } else {
      if (!usdcBalance || usdcBalance.lt(price)) {
        notify.error(`Insufficient USDC (need $${formatUsdc(price)})`);
        return;
      }
    }

    const key = `${pkgIdx}-${useZenova ? 'zenova' : 'usdc'}`;
    setBusyKey(key);

    try {
      // Approve if needed
      if (useZenova) {
        if (!zenovaAllowance || zenovaAllowance.lt(zAmount)) {
          setBusyLabel('Approving ZENOVA...');
          const ap = await zenovaW.approve(ADDR.storage, ethers.constants.MaxUint256);
          notify.info('Approval submitted...');
          await ap.wait();
          notify.success('ZENOVA approved');
        }
      } else {
        if (!usdcAllowance || usdcAllowance.lt(price)) {
          setBusyLabel('Approving USDC...');
          const ap = await usdcW.approve(ADDR.storage, ethers.constants.MaxUint256);
          notify.info('Approval submitted...');
          await ap.wait();
          notify.success('USDC approved');
        }
      }

      // Invest / Register + Invest — via sendTx for dry-run revert reasons
      setBusyLabel(isRegistered ? 'Investing...' : 'Registering...');
      const refBn = referrerId ? ethers.BigNumber.from(referrerId) : ethers.constants.Zero;

      let method, args, label;
      if (useZenova) {
        method = isRegistered ? 'investWithZenova' : 'registerAndInvestWithZenova';
        args   = isRegistered ? [pkgIdx] : [refBn, pkgIdx];
        label  = isRegistered ? 'Invest (ZENOVA)' : 'Register & Invest (ZENOVA)';
      } else {
        method = isRegistered ? 'invest' : 'registerAndInvest';
        args   = isRegistered ? [pkgIdx] : [refBn, pkgIdx];
        label  = isRegistered ? 'Invest (USDC)' : 'Register & Invest (USDC)';
      }

      await sendTx(logicW, method, args, { notify, label });
      if (!isRegistered) clearStoredRef();
      refresh();
      setReload((r) => r + 1);
    } catch (e) {
      notify.error(parseError(e));
    } finally {
      setBusyKey(null);
      setBusyLabel('');
    }
  };

  if (!packages) return <InlineLoader text="Loading packages..." />;

  return (
    <div>
      {/* Balances */}
      <div className="stats-grid-compact">
        <div className="stat-mini cyan">
          <div className="stat-mini-label"><span className="stat-mini-icon">💵</span>USDC Balance</div>
          <div className="stat-mini-value">${formatUsdc(usdcBalance)}</div>
        </div>
        <div className="stat-mini pink">
          <div className="stat-mini-label"><span className="stat-mini-icon">🪙</span>ZENOVA Balance</div>
          <div className="stat-mini-value">{formatZenova(zenovaBalance)}</div>
        </div>
      </div>

      {!isRegistered && (
        <div className="section-card">
          <div className="section-card-title">
            <span><span className="icon">🔗</span> Referrer</span>
          </div>
          <label className="input-label">Referrer ID (optional)</label>
          <input
            className="input"
            type="number"
            placeholder="e.g. 12345"
            value={referrerId}
            onChange={(e) => setReferrerId(e.target.value)}
          />
          <Alert type="info">Pay with USDC or ZENOVA — your referrer earns level income either way.</Alert>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {Array.from({ length: NUM_PACKAGES }).map((_, i) => {
          const price = packages.prices[i];
          const roi = packages.rois[i];
          const periods = packages.periods[i];
          const totalRoi = bnToNumber(roi) * bnToNumber(periods);
          const isPremium = i >= 7;
          const zQuote = zenovaQuotes && zenovaQuotes[i] ? zenovaQuotes[i].zenovaRequired : null;
          const busyUsdc = busyKey === `${i}-usdc`;
          const busyZen  = busyKey === `${i}-zenova`;
          const anyBusy = busyKey != null;

          return (
            <div key={i} className={`pkg-mini-card ${isPremium ? 'premium' : ''}`}>
              <div className="pkg-mini-header">
                <div>
                  <div className="pkg-mini-name">{PACKAGE_NAMES[i] || `Package ${i}`}</div>
                  <div className="pkg-mini-price">
                    ${formatUsdc(price)}<span className="curr">USDC</span>
                  </div>
                  {zQuote && !zQuote.isZero() && (
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
                      ≈ {formatZenova(zQuote)} ZENOVA
                    </div>
                  )}
                </div>
                <span className="pkg-mini-tag">{isPremium ? '👑 Premium' : `Level ${i + 1}`}</span>
              </div>

              <div className="pkg-mini-details">
                <span>⚡ {formatBps(roi)}/day</span>
                <span>📅 {bnToNumber(periods)} days</span>
                <span>📈 {(totalRoi / 100).toFixed(0)}% ROI</span>
                <span>🎯 4x cap</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => payAndInvest(i, false)}
                  disabled={anyBusy}
                >
                  {busyUsdc && <ButtonSpinner />}
                  {busyUsdc ? busyLabel : '💵 Pay USDC'}
                </button>
                <button
                  className={`btn ${isPremium ? 'btn-gold' : 'btn-primary'}`}
                  onClick={() => payAndInvest(i, true)}
                  disabled={anyBusy || !zQuote || zQuote.isZero()}
                >
                  {busyZen && <ButtonSpinner />}
                  {busyZen ? busyLabel : '🪙 Pay ZENOVA'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MyInvestmentsTab = () => {
  const { userId, isRegistered } = useUserData();
  const { viewsR } = useContracts();
  const [invs, setInvs] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!viewsR || !isRegistered || !userId) return;
      try {
        const list = await viewsR.getAllUserInvestments(userId);
        if (cancelled) return;
        setInvs(list);
      } catch (e) {}
    };
    load();
    return () => { cancelled = true; };
  }, [viewsR, isRegistered, userId]);

  if (!isRegistered) {
    return <EmptyState icon="💎" title="Not registered yet" text="Pick a package from the Packages tab to get started." />;
  }
  if (!invs) return <InlineLoader />;
  if (invs.length === 0) return <EmptyState icon="📭" title="No investments" text="Head to Packages to create your first one." />;

  return (
    <div>
      {invs.map((inv, i) => {
        const incRcv = Number(inv.totalIncomeReceived.toString());
        const cap = Number(inv.maxTotalIncome.toString());
        const pct = cap > 0 ? (incRcv / cap) * 100 : 0;
        const type = bnToNumber(inv.investmentType);

        return (
          <div key={i} className={`inv-mini ${!inv.active ? 'inactive' : ''}`}>
            <div className="inv-mini-header">
              <div>
                <div className="inv-mini-amt">${formatUsdc(inv.amount)}</div>
                <div className="inv-mini-pkg">{PACKAGE_NAMES[inv.packageId] || `Package ${inv.packageId}`}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span className={`badge ${inv.active ? 'badge-green' : 'badge-muted'}`}>
                  {inv.active ? 'Active' : 'Closed'}
                </span>
                <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4 }}>
                  {formatTimestamp(inv.timestamp)}
                </div>
              </div>
            </div>

            <div className="rank-req-row">
              <span className="k">Type</span>
              <span className="v" style={{ color: 'var(--text-primary)' }}>{INV_TYPES[type] || 'Real'}</span>
            </div>
            <div className="rank-req-row">
              <span className="k">ROI Earned</span>
              <span className="v" style={{ color: 'var(--neon-cyan)' }}>${formatUsdc(inv.roiEarned)}</span>
            </div>
            <div className="rank-req-row">
              <span className="k">Income Received</span>
              <span className="v" style={{ color: 'var(--neon-green)' }}>${formatUsdc(inv.totalIncomeReceived)}</span>
            </div>
            <div className="rank-req-row">
              <span className="k">Max Income (4x)</span>
              <span className="v">${formatUsdc(inv.maxTotalIncome)}</span>
            </div>

            <div className="inv-mini-progress-lbl">
              <span>Ceiling Progress</span>
              <span style={{ color: 'var(--neon-cyan)', fontWeight: 700 }}>{pct.toFixed(1)}%</span>
            </div>
            <div className="inv-progress-track">
              <div className="inv-progress-fill" style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};
